/**
 * Dense linear 9-stage curriculum — beginner → mastery recital.
 * Blueprint core titles kept; additional classes deepen each stage.
 * Every pieceId must exist in BUILTIN_LIBRARY.
 */

import type { TutorClass, TutorStage } from "./types.js";
import { ADVANCED_STAGES } from "./curriculumAdvanced.js";
import { MASTERY_STAGES } from "./curriculumMastery.js";
import { PRO_STAGES } from "./curriculumPro.js";
import { normalizeTutorClass } from "./kindAssessment.js";

function cls(partial: TutorClass): TutorClass {
  return normalizeTutorClass(partial);
}

const STAGE_1: TutorClass[] = [
  cls({
    id: "class-1-1",
    stage: 1,
    code: "1.1",
    title: "Fixed C Position",
    kind: "drill",
    defaultMinutes: 20,
    minMinutes: 10,
    maxMinutes: 40,
    description:
      "RH C-position muscle memory — walks, holds, skips and fresh combos for the full session time.",
    segments: [
      { pieceId: "ex-five-finger-c", baseLoops: 3, label: "Five-finger walk" },
      { pieceId: "ex-repeat-hold-c", baseLoops: 2, label: "Repeat & hold" },
      { pieceId: "ex-finger-skips-c", baseLoops: 3, label: "Skip shapes" },
      {
        pieceId: "ex-five-finger-c",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 56,
        label: "Locked walk",
      },
    ],
  }),
  cls({
    id: "class-1-2",
    stage: 1,
    code: "1.2",
    title: "Left Hand Mirroring",
    kind: "drill",
    defaultMinutes: 25,
    minMinutes: 12,
    maxMinutes: 45,
    description:
      "Activate LH five-finger C position only — same shape as RH. Full LH scale comes after thumb-tuck prep.",
    segments: [
      { pieceId: "ex-five-finger-c-lh", baseLoops: 5, label: "LH five-finger walk" },
      { pieceId: "ex-repeat-hold-c-lh", baseLoops: 4, label: "LH repeat & hold" },
      {
        pieceId: "ex-finger-skips-c-lh",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 58,
        label: "LH skips locked",
      },
    ],
  }),
  cls({
    id: "class-1-3",
    stage: 1,
    code: "1.3",
    title: "Rhythmic Basics",
    kind: "rhythm",
    defaultMinutes: 30,
    minMinutes: 15,
    maxMinutes: 50,
    description: "Whole/half/quarter values locked to the metronome.",
    segments: [
      {
        pieceId: "rhythm-whole-half-c",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 66,
        label: "Long values",
      },
      {
        pieceId: "rhythm-quarters-c",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 76,
        label: "Steady quarters",
      },
      {
        pieceId: "melody-hot-cross",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 80,
        label: "Hot Cross rhythm",
      },
    ],
  }),
  cls({
    id: "class-1-4",
    stage: 1,
    code: "1.4",
    title: "Finger Skips & Shape Prep",
    kind: "drill",
    defaultMinutes: 20,
    minMinutes: 10,
    maxMinutes: 35,
    description: "Skips inside C position — chord readiness.",
    segments: [
      { pieceId: "ex-finger-skips-c", baseLoops: 5, label: "Skip patterns" },
      {
        pieceId: "ex-finger-skips-c",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 64,
        label: "Skips locked",
      },
    ],
  }),
  cls({
    id: "class-1-5",
    stage: 1,
    code: "1.5",
    title: "First Melodies (C Position)",
    kind: "drill",
    defaultMinutes: 30,
    minMinutes: 15,
    maxMinutes: 50,
    description: "Mary / Hot Cross / Twinkle — still in five-finger land.",
    segments: [
      { pieceId: "melody-mary-lamb", baseLoops: 4, label: "Mary" },
      { pieceId: "melody-hot-cross", baseLoops: 3, label: "Hot Cross" },
      {
        pieceId: "melody-twinkle",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 84,
        label: "Twinkle locked",
      },
    ],
  }),
  cls({
    id: "class-1-6",
    stage: 1,
    code: "1.6",
    title: "Eighths & Subdivisions",
    kind: "rhythm",
    defaultMinutes: 25,
    minMinutes: 12,
    maxMinutes: 40,
    description: "Even eighth walks before scales expand.",
    segments: [
      {
        pieceId: "rhythm-eighths-c",
        baseLoops: 5,
        metronomeLocked: true,
        bpm: 72,
        label: "Eighth walk",
      },
      {
        pieceId: "rhythm-quarters-c",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 88,
        label: "Quarter contrast",
      },
    ],
  }),
  cls({
    id: "class-1-7",
    stage: 1,
    code: "1.7",
    title: "Hands Separate Scales",
    kind: "scale",
    defaultMinutes: 30,
    minMinutes: 15,
    maxMinutes: 50,
    description: "RH then LH C major — prepare Stage 2 thumb tuck.",
    segments: [
      { pieceId: "scale-c-major-rh", baseLoops: 5, bpm: 72, label: "RH C major" },
      { pieceId: "scale-c-major-lh", baseLoops: 5, bpm: 68, label: "LH C major" },
      {
        pieceId: "scale-c-major-rh",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 80,
        label: "RH locked",
      },
    ],
  }),
  cls({
    id: "class-1-8",
    stage: 1,
    code: "1.8",
    title: "Stage 1 Recital Mix",
    kind: "drill",
    defaultMinutes: 35,
    minMinutes: 18,
    maxMinutes: 55,
    description: "Integrate drills + Ode to Joy for gatekeeper readiness.",
    segments: [
      { pieceId: "ex-five-finger-c", baseLoops: 2, label: "Quick warm-up" },
      { pieceId: "melody-ode-to-joy", baseLoops: 4, label: "Ode to Joy" },
      {
        pieceId: "melody-ode-to-joy",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 92,
        label: "Ode locked",
      },
      {
        pieceId: "scale-c-major-rh",
        baseLoops: 2,
        metronomeLocked: true,
        bpm: 84,
        label: "Exit scale",
      },
    ],
  }),
  cls({
    id: "class-1-9",
    stage: 1,
    code: "1.9",
    title: "Both Hands Mirror Intro",
    kind: "polyphony",
    defaultMinutes: 30,
    minMinutes: 15,
    maxMinutes: 50,
    description: "First hands-together: mirrored C-position notes at the same time.",
    segments: [
      { pieceId: "ex-five-finger-c", baseLoops: 2, label: "RH refresh" },
      { pieceId: "ex-five-finger-c-lh", baseLoops: 2, label: "LH refresh" },
      { pieceId: "both-c-position-mirrors", baseLoops: 5, label: "Mirrors" },
      {
        pieceId: "both-c-position-mirrors",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 62,
        label: "Mirrors locked",
      },
    ],
  }),
  cls({
    id: "class-1-10",
    stage: 1,
    code: "1.10",
    title: "Familiar Melody Boost",
    kind: "drill",
    defaultMinutes: 25,
    minMinutes: 12,
    maxMinutes: 40,
    description: "Jingle + Twinkle for fluency before Stage 2.",
    segments: [
      { pieceId: "melody-jingle-rh", baseLoops: 4, label: "Jingle" },
      { pieceId: "melody-twinkle", baseLoops: 3, label: "Twinkle" },
      {
        pieceId: "melody-jingle-rh",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 104,
        label: "Jingle locked",
      },
    ],
  }),
];

const STAGE_2: TutorClass[] = [
  cls({
    id: "class-2-1",
    stage: 2,
    code: "2.1",
    title: "The Thumb Tuck",
    kind: "scale",
    defaultMinutes: 25,
    minMinutes: 12,
    maxMinutes: 45,
    description: "Move beyond five keys — 1-octave then 2-octave C.",
    segments: [
      { pieceId: "scale-c-major-rh", baseLoops: 4, bpm: 76, label: "1 octave refresh" },
      { pieceId: "scale-c-major-2oct-rh", baseLoops: 4, bpm: 66, label: "2 octaves" },
      {
        pieceId: "scale-c-major-2oct-rh",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 76,
        label: "2-oct locked",
      },
    ],
  }),
  cls({
    id: "class-2-2",
    stage: 2,
    code: "2.2",
    title: "Black Keys Introduction",
    kind: "scale",
    defaultMinutes: 30,
    minMinutes: 15,
    maxMinutes: 50,
    description: "Sharps (#) and flats (♭) via landmarks + G/F majors.",
    segments: [
      { pieceId: "ex-black-keys-fs", baseLoops: 4, label: "F# / Bb landmarks" },
      { pieceId: "scale-g-major-rh", baseLoops: 4, label: "G major RH" },
      {
        pieceId: "scale-f-major-rh",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 74,
        label: "F major locked",
      },
    ],
  }),
  cls({
    id: "class-2-3",
    stage: 2,
    code: "2.3",
    title: "Syncopation Drill",
    kind: "rhythm",
    defaultMinutes: 30,
    minMinutes: 15,
    maxMinutes: 50,
    description: "Off-beat and anticipation patterns with metronome.",
    segments: [
      {
        pieceId: "rhythm-syncop-offbeat",
        baseLoops: 5,
        metronomeLocked: true,
        bpm: 72,
        label: "Off-beat &s",
      },
      {
        pieceId: "rhythm-syncop-anticipate",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 76,
        label: "Anticipations",
      },
      {
        pieceId: "arp-c-major",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 70,
        label: "Arp stabilize",
      },
    ],
  }),
  cls({
    id: "class-2-4",
    stage: 2,
    code: "2.4",
    title: "G Major Hands Separate",
    kind: "scale",
    defaultMinutes: 25,
    minMinutes: 12,
    maxMinutes: 40,
    description: "G major RH then LH on F♯ — build each hand before combining later.",
    segments: [
      { pieceId: "scale-g-major-rh", baseLoops: 4, label: "RH G" },
      { pieceId: "scale-g-major-lh", baseLoops: 4, label: "LH G" },
      {
        pieceId: "scale-g-major-rh",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 84,
        label: "RH locked",
      },
    ],
  }),
  cls({
    id: "class-2-5",
    stage: 2,
    code: "2.5",
    title: "D Major & Chromatic Prep",
    kind: "scale",
    defaultMinutes: 30,
    minMinutes: 15,
    maxMinutes: 45,
    description: "Two sharps + short chromatic control.",
    segments: [
      { pieceId: "ex-chromatic-c-e", baseLoops: 4, label: "Chromatic C–E" },
      { pieceId: "scale-d-major-rh", baseLoops: 5, label: "D major" },
      {
        pieceId: "scale-d-major-rh",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 78,
        label: "D locked",
      },
    ],
  }),
  cls({
    id: "class-2-6",
    stage: 2,
    code: "2.6",
    title: "Arpeggio Family (C / G / F)",
    kind: "scale",
    defaultMinutes: 30,
    minMinutes: 15,
    maxMinutes: 50,
    description: "Broken triads across primary keys.",
    segments: [
      { pieceId: "arp-c-major", baseLoops: 4, label: "C arp" },
      { pieceId: "arp-g-major", baseLoops: 4, label: "G arp" },
      {
        pieceId: "arp-f-major",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 68,
        label: "F arp locked",
      },
    ],
  }),
  cls({
    id: "class-2-7",
    stage: 2,
    code: "2.7",
    title: "A Minor Relative",
    kind: "scale",
    defaultMinutes: 25,
    minMinutes: 12,
    maxMinutes: 40,
    description: "Natural minor as C’s relative — ear color shift.",
    segments: [
      { pieceId: "scale-a-minor-rh", baseLoops: 5, label: "A minor" },
      {
        pieceId: "scale-a-minor-rh",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 76,
        label: "A minor locked",
      },
      { pieceId: "melody-frere-jacques", baseLoops: 3, label: "Frère Jacques" },
    ],
  }),
  cls({
    id: "class-2-8",
    stage: 2,
    code: "2.8",
    title: "Stage 2 Integration",
    kind: "rhythm",
    defaultMinutes: 35,
    minMinutes: 18,
    maxMinutes: 55,
    description: "Black keys + syncopation + melody under time.",
    segments: [
      { pieceId: "scale-g-major-rh", baseLoops: 2, label: "G warm-up" },
      {
        pieceId: "rhythm-syncop-offbeat",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 80,
        label: "Syncop recall",
      },
      {
        pieceId: "melody-frere-jacques",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 92,
        label: "Frère locked",
      },
      { pieceId: "arp-c-major", baseLoops: 2, metronomeLocked: true, bpm: 76, label: "Exit arp" },
    ],
  }),
  cls({
    id: "class-2-9",
    stage: 2,
    code: "2.9",
    title: "LH Depth Lab",
    kind: "scale",
    defaultMinutes: 35,
    minMinutes: 18,
    maxMinutes: 55,
    description: "Extra LH scales (F/D) + root–fifth accompaniment language.",
    segments: [
      { pieceId: "scale-f-major-lh", baseLoops: 4, bpm: 70, label: "LH F major" },
      { pieceId: "scale-d-major-lh", baseLoops: 4, bpm: 70, label: "LH D major" },
      { pieceId: "lh-chord-root-fifth", baseLoops: 4, label: "Root–fifth march" },
      {
        pieceId: "ex-black-keys-lh",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 68,
        label: "LH black keys",
      },
    ],
  }),
  cls({
    id: "class-2-10",
    stage: 2,
    code: "2.10",
    title: "Contrary + Duet Seeds",
    kind: "polyphony",
    defaultMinutes: 35,
    minMinutes: 18,
    maxMinutes: 55,
    description: "Contrary motion + Twinkle duet — true hands-together growth.",
    segments: [
      { pieceId: "both-contrary-c", baseLoops: 5, label: "Contrary C" },
      { pieceId: "both-twinkle-duet", baseLoops: 4, label: "Twinkle duet" },
      {
        pieceId: "both-contrary-c",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 72,
        label: "Contrary locked",
      },
    ],
  }),
];

const STAGE_3: TutorClass[] = [
  cls({
    id: "class-3-1",
    stage: 3,
    code: "3.1",
    title: "Triad Foundations",
    kind: "chord",
    defaultMinutes: 30,
    minMinutes: 15,
    maxMinutes: 50,
    description: "Major and minor triad block quality.",
    segments: [
      { pieceId: "chord-c-major-triad", baseLoops: 5, label: "C major" },
      { pieceId: "chord-a-minor-triad", baseLoops: 4, label: "A minor" },
      {
        pieceId: "chord-g-major-triad",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 60,
        label: "G major locked",
      },
    ],
  }),
  cls({
    id: "class-3-2",
    stage: 3,
    code: "3.2",
    title: "Chord Inversions",
    kind: "chord",
    defaultMinutes: 30,
    minMinutes: 15,
    maxMinutes: 50,
    description: "Root → 1st → 2nd inversion shapes on C.",
    segments: [
      { pieceId: "chord-c-major-triad", baseLoops: 3, label: "Root" },
      { pieceId: "chord-c-inv1", baseLoops: 5, label: "1st inversion" },
      {
        pieceId: "chord-c-inv2",
        baseLoops: 5,
        metronomeLocked: true,
        bpm: 64,
        label: "2nd locked",
      },
    ],
  }),
  cls({
    id: "class-3-3",
    stage: 3,
    code: "3.3",
    title: "Hand Independence",
    kind: "independence",
    defaultMinutes: 45,
    minMinutes: 20,
    maxMinutes: 70,
    description: "LH drone / alternation while RH carries melody.",
    segments: [
      { pieceId: "both-hand-alternation", baseLoops: 4, label: "Alternation" },
      { pieceId: "both-bass-drone-melody", baseLoops: 4, label: "Drone + melody" },
      {
        pieceId: "both-bass-drone-melody",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 78,
        label: "Independence locked",
      },
    ],
  }),
  cls({
    id: "class-3-4",
    stage: 3,
    code: "3.4",
    title: "Primary Triads F & G",
    kind: "chord",
    defaultMinutes: 30,
    minMinutes: 15,
    maxMinutes: 45,
    description: "IV and V as solid blocks — cadential building blocks.",
    segments: [
      { pieceId: "chord-f-major-triad", baseLoops: 5, label: "F major" },
      { pieceId: "chord-g-major-triad", baseLoops: 5, label: "G major" },
      {
        pieceId: "chord-i-iv-v-c",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 64,
        label: "I–IV–V–I",
      },
    ],
  }),
  cls({
    id: "class-3-5",
    stage: 3,
    code: "3.5",
    title: "Broken Chord Fluency",
    kind: "chord",
    defaultMinutes: 30,
    minMinutes: 15,
    maxMinutes: 50,
    description: "Broken triad fluency across I–IV–V (root–3–5–3 patterns).",
    segments: [
      { pieceId: "arp-broken-triads-c", baseLoops: 5, label: "Broken I–IV–V" },
      {
        pieceId: "arp-broken-triads-c",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 76,
        label: "Broken locked",
      },
      { pieceId: "arp-c-major", baseLoops: 3, label: "C arp polish" },
    ],
  }),
  cls({
    id: "class-3-6",
    stage: 3,
    code: "3.6",
    title: "Cadences & Melody Overlay",
    kind: "independence",
    defaultMinutes: 35,
    minMinutes: 18,
    maxMinutes: 55,
    description: "Cadence blocks, then Ode melody, then alternation — build toward polyphony feel.",
    segments: [
      { pieceId: "chord-i-iv-v-c", baseLoops: 4, label: "Cadence blocks" },
      { pieceId: "melody-ode-to-joy", baseLoops: 3, label: "RH Ode" },
      {
        pieceId: "both-hand-alternation",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 80,
        label: "Hands locked",
      },
    ],
  }),
  cls({
    id: "class-3-7",
    stage: 3,
    code: "3.7",
    title: "Stage 3 Harmony Lab",
    kind: "chord",
    defaultMinutes: 40,
    minMinutes: 20,
    maxMinutes: 60,
    description: "Full triad toolkit under exam pressure pacing.",
    segments: [
      { pieceId: "chord-c-major-triad", baseLoops: 2, label: "C" },
      { pieceId: "chord-c-inv1", baseLoops: 2, label: "Inv1" },
      { pieceId: "chord-c-inv2", baseLoops: 2, label: "Inv2" },
      {
        pieceId: "chord-i-iv-v-c",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 70,
        label: "Cadence exam pace",
      },
      {
        pieceId: "arp-broken-triads-c",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 80,
        label: "Broken exit",
      },
    ],
  }),
  cls({
    id: "class-3-8",
    stage: 3,
    code: "3.8",
    title: "Alberti Prep → Ode with Bass",
    kind: "polyphony",
    defaultMinutes: 40,
    minMinutes: 20,
    maxMinutes: 60,
    description: "Learn Alberti LH alone, then Ode with root–fifth bass, then block I–V.",
    segments: [
      { pieceId: "lh-alberti-c", baseLoops: 4, label: "Alberti alone" },
      { pieceId: "both-ode-with-bass", baseLoops: 4, label: "Ode + bass" },
      { pieceId: "both-block-i-v", baseLoops: 4, label: "Block I–V" },
      {
        pieceId: "both-ode-with-bass",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 84,
        label: "Ode locked",
      },
    ],
  }),
  cls({
    id: "class-3-9",
    stage: 3,
    code: "3.9",
    title: "Parallel Independence",
    kind: "polyphony",
    defaultMinutes: 35,
    minMinutes: 18,
    maxMinutes: 55,
    description: "Parallel 3rds both hands + prior drone/alternation recall.",
    segments: [
      { pieceId: "both-parallel-thirds", baseLoops: 5, label: "Parallel 3rds" },
      { pieceId: "both-bass-drone-melody", baseLoops: 3, label: "Drone recall" },
      { pieceId: "both-hand-alternation", baseLoops: 3, label: "Alternation recall" },
      {
        pieceId: "both-parallel-thirds",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 74,
        label: "3rds locked",
      },
    ],
  }),
];

const STAGE_4: TutorClass[] = [
  cls({
    id: "class-4-1",
    stage: 4,
    code: "4.1",
    title: "Sheet Music Translation",
    kind: "sight",
    defaultMinutes: 35,
    minMinutes: 18,
    maxMinutes: 55,
    description: "Sight lines across meters and familiar tunes.",
    segments: [
      { pieceId: "melody-ode-to-joy", baseLoops: 3, label: "Sight A — Ode" },
      { pieceId: "melody-london-bridge", baseLoops: 4, label: "Sight B — London" },
      {
        pieceId: "melody-sight-mix-a",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 84,
        label: "Waltz sight",
      },
    ],
  }),
  cls({
    id: "class-4-2",
    stage: 4,
    code: "4.2",
    title: "The Blind Play Engine",
    kind: "blind",
    defaultMinutes: 35,
    minMinutes: 18,
    maxMinutes: 55,
    description: "Muscle memory drills — minimize label dependency (exam is fully blind).",
    segments: [
      {
        pieceId: "ex-blind-scale-c",
        baseLoops: 5,
        metronomeLocked: true,
        bpm: 88,
        label: "Blind scale",
      },
      {
        pieceId: "ex-blind-arp-c",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 84,
        label: "Blind arp",
      },
    ],
  }),
  cls({
    id: "class-4-3",
    stage: 4,
    code: "4.3",
    title: "Interval Recognition Drill",
    kind: "sight",
    defaultMinutes: 30,
    minMinutes: 15,
    maxMinutes: 45,
    description: "Leaps of 3rds–octaves for ear + reading.",
    segments: [
      { pieceId: "ex-interval-jumps", baseLoops: 5, label: "Jumps" },
      {
        pieceId: "ex-interval-jumps",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 70,
        label: "Jumps locked",
      },
    ],
  }),
  cls({
    id: "class-4-4",
    stage: 4,
    code: "4.4",
    title: "Ear Echo Patterns",
    kind: "ear",
    defaultMinutes: 30,
    minMinutes: 15,
    maxMinutes: 45,
    description: "Short motivic echoes — hear once, then play from memory (no key/staff guide).",
    assessment: "ear",
    skills: ["ear echo", "aural memory", "motivic recall"],
    segments: [
      { pieceId: "ex-ear-echo-c", baseLoops: 5, label: "Echo cells", focus: "memory" },
      {
        pieceId: "ex-ear-echo-c",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 76,
        label: "Echo locked",
        focus: "memory",
      },
      { pieceId: "melody-twinkle", baseLoops: 2, label: "Recall Twinkle", focus: "memory" },
    ],
  }),
  cls({
    id: "class-4-5",
    stage: 4,
    code: "4.5",
    title: "Folk Sight Marathon",
    kind: "sight",
    defaultMinutes: 35,
    minMinutes: 18,
    maxMinutes: 55,
    description: "Chain folk melodies without stopping between takes.",
    segments: [
      { pieceId: "melody-frere-jacques", baseLoops: 3, label: "Frère" },
      { pieceId: "melody-london-bridge", baseLoops: 3, label: "London" },
      { pieceId: "melody-mary-lamb", baseLoops: 2, label: "Mary" },
      {
        pieceId: "melody-sight-mix-a",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 90,
        label: "Mix locked",
      },
    ],
  }),
  cls({
    id: "class-4-6",
    stage: 4,
    code: "4.6",
    title: "Blind + Sight Final",
    kind: "blind",
    defaultMinutes: 40,
    minMinutes: 20,
    maxMinutes: 60,
    description: "Alternate helper-on sight with helper-off memory.",
    segments: [
      { pieceId: "melody-ode-to-joy", baseLoops: 2, label: "Sight Ode" },
      {
        pieceId: "ex-blind-scale-c",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 96,
        label: "Blind scale",
      },
      {
        pieceId: "ex-blind-arp-c",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 92,
        label: "Blind arp",
      },
      {
        pieceId: "melody-london-bridge",
        baseLoops: 2,
        metronomeLocked: true,
        bpm: 96,
        label: "Sight exit",
      },
    ],
  }),
  cls({
    id: "class-4-7",
    stage: 4,
    code: "4.7",
    title: "Hands-Together Sight",
    kind: "sight",
    defaultMinutes: 35,
    minMinutes: 18,
    maxMinutes: 55,
    description: "Contrary scales + sight duet — reading while coordinating.",
    segments: [
      { pieceId: "both-scale-contrary-1oct", baseLoops: 4, label: "Contrary scale" },
      { pieceId: "both-sight-duet-lite", baseLoops: 5, label: "Sight duet" },
      {
        pieceId: "both-scale-contrary-1oct",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 80,
        label: "Contrary locked",
      },
    ],
  }),
];

const STAGE_5: TutorClass[] = [
  cls({
    id: "class-5-1",
    stage: 5,
    code: "5.1",
    title: "Polyrhythm Prep",
    kind: "polyrhythm",
    defaultMinutes: 45,
    minMinutes: 20,
    maxMinutes: 70,
    description: "One-hand 3-against-2 and 4-against-3 preparatory groupings before true two-hand poly.",
    segments: [
      {
        pieceId: "rhythm-poly-3against2-prep",
        baseLoops: 5,
        metronomeLocked: true,
        bpm: 56,
        label: "3 vs 2 prep",
      },
      {
        pieceId: "rhythm-poly-4against3-prep",
        baseLoops: 5,
        metronomeLocked: true,
        bpm: 52,
        label: "4 vs 3 prep",
      },
    ],
  }),
  cls({
    id: "class-5-2",
    stage: 5,
    code: "5.2",
    title: "Rapid Scale Bursts",
    kind: "speed",
    defaultMinutes: 45,
    minMinutes: 20,
    maxMinutes: 70,
    description: "High NPM sixteenth bursts in C and G.",
    segments: [
      {
        pieceId: "scale-burst-c-sixteenths",
        baseLoops: 5,
        metronomeLocked: true,
        bpm: 96,
        label: "C burst",
      },
      {
        pieceId: "scale-burst-g-sixteenths",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 92,
        label: "G burst",
      },
    ],
  }),
  cls({
    id: "class-5-3",
    stage: 5,
    code: "5.3",
    title: "Arpeggio Velocity",
    kind: "speed",
    defaultMinutes: 35,
    minMinutes: 18,
    maxMinutes: 55,
    description: "Wide arpeggios at competition tempo.",
    segments: [
      {
        pieceId: "arp-burst-c-2oct",
        baseLoops: 5,
        metronomeLocked: true,
        bpm: 100,
        label: "2-oct burst",
      },
      {
        pieceId: "arp-c-major",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 110,
        label: "Compact arp speed",
      },
    ],
  }),
  cls({
    id: "class-5-4",
    stage: 5,
    code: "5.4",
    title: "Chromatic Agility",
    kind: "speed",
    defaultMinutes: 30,
    minMinutes: 15,
    maxMinutes: 50,
    description: "Half-step bursts for finger independence at speed.",
    segments: [
      {
        pieceId: "ex-chromatic-burst",
        baseLoops: 5,
        metronomeLocked: true,
        bpm: 88,
        label: "Chromatic burst",
      },
      {
        pieceId: "ex-chromatic-c-e",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 100,
        label: "Short chromatic sprint",
      },
    ],
  }),
  cls({
    id: "class-5-5",
    stage: 5,
    code: "5.5",
    title: "Broken Thirds Articulation",
    kind: "speed",
    defaultMinutes: 35,
    minMinutes: 18,
    maxMinutes: 55,
    description: "Diatonic thirds — even tone at elevated BPM.",
    segments: [
      { pieceId: "ex-scales-in-thirds-c", baseLoops: 4, bpm: 80, label: "Thirds moderate" },
      {
        pieceId: "ex-scales-in-thirds-c",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 92,
        label: "Thirds locked",
      },
    ],
  }),
  cls({
    id: "class-5-6",
    stage: 5,
    code: "5.6",
    title: "Elite Capstone",
    kind: "speed",
    defaultMinutes: 50,
    minMinutes: 25,
    maxMinutes: 80,
    description: "Polyrhythm + bursts + thirds — Stage 5 gatekeeper prep.",
    segments: [
      {
        pieceId: "rhythm-poly-3against2-prep",
        baseLoops: 2,
        metronomeLocked: true,
        bpm: 60,
        label: "Poly warm",
      },
      {
        pieceId: "scale-burst-c-sixteenths",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 104,
        label: "C burst",
      },
      {
        pieceId: "scale-burst-g-sixteenths",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 100,
        label: "G burst",
      },
      {
        pieceId: "arp-burst-c-2oct",
        baseLoops: 2,
        metronomeLocked: true,
        bpm: 108,
        label: "Arp exit",
      },
      {
        pieceId: "ex-scales-in-thirds-c",
        baseLoops: 2,
        metronomeLocked: true,
        bpm: 96,
        label: "Thirds exit",
      },
    ],
  }),
  cls({
    id: "class-5-7",
    stage: 5,
    code: "5.7",
    title: "Elite Both-Hands Capstone",
    kind: "polyphony",
    defaultMinutes: 45,
    minMinutes: 22,
    maxMinutes: 70,
    description: "Fast alternation + contrary + duet pressure before final gatekeeper.",
    segments: [
      { pieceId: "both-speed-alternation", baseLoops: 5, metronomeLocked: true, bpm: 104, label: "Fast alt" },
      { pieceId: "both-scale-contrary-1oct", baseLoops: 3, metronomeLocked: true, bpm: 92, label: "Contrary speed" },
      { pieceId: "both-ode-with-bass", baseLoops: 3, metronomeLocked: true, bpm: 96, label: "Ode + bass" },
      { pieceId: "both-block-i-v", baseLoops: 2, metronomeLocked: true, bpm: 88, label: "Block exit" },
    ],
  }),
];

export const CURRICULUM: TutorStage[] = [
  {
    id: 1,
    title: "The Home Row Foundations",
    blurb: "Beginner — C position, LH, rhythm, melodies, first hands-together (~4+ hrs default).",
    examId: "exam-stage-1",
    classes: STAGE_1,
  },
  {
    id: 2,
    title: "Expanding Horizons & Accidentals",
    blurb: "Intermediate — thumb tuck, black keys, syncopation, new keys (~4 hrs default).",
    examId: "exam-stage-2",
    classes: STAGE_2,
  },
  {
    id: 3,
    title: "Harmony, Chords & Polyphony",
    blurb: "Advanced — triads, inversions, cadences, independence (~4 hrs default).",
    examId: "exam-stage-3",
    classes: STAGE_3,
  },
  {
    id: 4,
    title: "Sight-Reading & Ear Training",
    blurb: "Super-advanced — sight, intervals, echo, blind play (~3.5 hrs default).",
    examId: "exam-stage-4",
    classes: STAGE_4,
  },
  {
    id: 5,
    title: "Masterclass Articulation & Speed",
    blurb: "Elite — polyrhythms, bursts, chromatic agility (~4 hrs default).",
    examId: "exam-stage-5",
    classes: STAGE_5,
  },
  ...ADVANCED_STAGES,
  ...MASTERY_STAGES,
  ...PRO_STAGES,
];

export function listStages(): TutorStage[] {
  return CURRICULUM;
}

export function getStage(stageId: number): TutorStage | undefined {
  return CURRICULUM.find((s) => s.id === stageId);
}

export function getClassById(classId: string) {
  for (const stage of CURRICULUM) {
    const found = stage.classes.find((c) => c.id === classId);
    if (found) return normalizeTutorClass(found);
  }
  return undefined;
}

export function listAllClasses() {
  return CURRICULUM.flatMap((s) => s.classes.map(normalizeTutorClass));
}

/** Classes that count toward the Course Completion Certificate (stages 1–9). */
export function listCertificateClasses() {
  return listAllClasses().filter((c) => c.stage <= 9);
}

/** Sum of defaultMinutes across all classes (syllabus depth metric). */
export function totalDefaultMinutes(): number {
  return listAllClasses().reduce((n, c) => n + c.defaultMinutes, 0);
}
